import React, { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { DataTable } from "@/components/tables";
import { Paginate } from "@/components/paginate";
import { Button, Tooltip, Typography } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { IconFileText, IconSend, IconSparkles } from "@tabler/icons-react";
import { getPrompts } from "@/helpers/apis";
import { useTenantInfo, useUsersWithServiceUser } from "@/helpers/hooks";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { PromptResponse } from "./types";
import PromptActionsMenu from "./PromptActionsMenu";
import { ColorInsightTags } from "@/components/tags";
import useUserGroups from "@/pages/settings/components/userGroups/useUserGroups";
import { formatUserName } from "@/helpers/utils";
import { TableHeaderType } from "@/components/tables/types";
import { StudioTracking } from "@/lib/tracking/studio";

interface PromptsListProps {
  onUsePrompt: (promptText: string) => void;
}

const PromptsList: React.FC<PromptsListProps> = ({ onUsePrompt }) => {
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const { users } = useUsersWithServiceUser();
  const { getTeamNames } = useUserGroups();
  const { rbacEnabled } = useTenantInfo();

  // Get filter values from URL params
  const type = searchParams.getAll("type");
  const teams = searchParams.getAll("teams");
  const owners = searchParams.getAll("owners");
  const tags = searchParams.getAll("tags");
  const sort = searchParams.get("sort") || "created_at";
  const search = searchParams.get("search") || "";
  const page = parseInt(searchParams.get("page") || "1");
  const pageSize = parseInt(searchParams.get("pageSize") || "20");

  // Fetch prompts
  const {
    data: promptsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["prompts", { type, teams, owners, tags, sort, search, page, pageSize }],
    queryFn: () => {
      // Build params object, only including defined values
      const params: Record<string, unknown> = {
        page,
        page_size: pageSize,
      };

      if (type.length > 0) params.type = type;
      if (teams.length > 0) params.teams = teams;
      if (owners.length > 0) params.owners = owners;
      if (tags.length > 0) params.tags = tags;
      if (sort) params.sort = sort as "created_at" | "last_used_at" | "updated_at";
      if (search) params.search_text = search;

      return getPrompts(params);
    },
  });

  const prompts = promptsData?.items || [];
  const total = promptsData?.total || 0;
  const totalPages = Math.ceil((promptsData?.total || 0) / pageSize);

  // Memoize data object to prevent new reference on every render
  const tableData = useMemo(() => ({ items: prompts }), [prompts]);

  // Memoize pagination callbacks
  const handlePageClick = useCallback(
    (newPage: number) => {
      updateSearchParamsByKey({ page: (newPage + 1).toString() });
    },
    [updateSearchParamsByKey]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      updateSearchParamsByKey({ page: "1", pageSize: newPageSize.toString() });
    },
    [updateSearchParamsByKey]
  );

  // Helper function to get user name
  const getUserName = useCallback(
    (userId: number, isPublic: boolean) => {
      if (isPublic) return "Altimate AI";
      const user = users?.find(u => u.id.toString() === userId.toString());
      if (user?.email === "serviceuser@altimate.ai") return "Altimate AI";
      return formatUserName(user);
    },
    [users]
  );

  // Table headers
  const headers = useMemo(() => {
    return [
      {
        id: 1,
        uid: "prompt",
        label: "Prompt",
        width: "35%",
        render: (item: PromptResponse) => (
          <Stack direction="row" align="center" gap={"lg"}>
            <div className={"al-text-muted-foreground"}>
              {item.is_public ? (
                <Tooltip title="Default Prompt">
                  <IconSparkles className="al-size-5" />
                </Tooltip>
              ) : (
                <Tooltip title="Saved Prompt">
                  <IconFileText className="al-size-5" />
                </Tooltip>
              )}
            </div>
            <Typography variant="body" weight="medium" size="sm">
              {item.prompt_text}
            </Typography>
          </Stack>
        ),
      },
      {
        id: 2,
        uid: "tags",
        label: "Tags",
        width: "15%",
        render: (item: PromptResponse) => <ColorInsightTags tags={item.tags} showMore />,
      },
      {
        id: 3,
        uid: "owner",
        label: "Owner",
        width: "15%",
        render: (item: PromptResponse) => (
          <span className="al-text-sm">{getUserName(item.created_by, item.is_public)}</span>
        ),
      },
      ...(rbacEnabled
        ? [
            {
              id: 4,
              uid: "team",
              label: "Team",
              width: "15%",
              render: (item: PromptResponse) => (
                <ColorInsightTags tags={getTeamNames(item.teams) || []} showMore />
              ),
            },
          ]
        : []),
      {
        id: 5,
        uid: "use_prompt",
        label: "",
        width: "8%",
        render: (item: PromptResponse) => (
          <Stack align={"center"}>
            <Button
              variant="outline"
              size="sm"
              title="Use Prompt"
              onClick={e => {
                e.stopPropagation();
                StudioTracking.promptUsed(item.id);
                onUsePrompt(item.prompt_text);
              }}
            >
              <IconSend className={"al-size-4"} />
            </Button>
            <PromptActionsMenu prompt={item} />
          </Stack>
        ),
      },
    ] as TableHeaderType<PromptResponse>[];
  }, [getUserName, getTeamNames, onUsePrompt, rbacEnabled]);

  if (error) {
    return (
      <div className="al-p-4 al-text-center">
        <div className="al-text-red-500">Error loading prompts</div>
      </div>
    );
  }

  if (!isLoading && prompts.length === 0) {
    return (
      <div className="al-p-4 al-text-center">
        <div className="al-text-gray-500">No prompts found</div>
      </div>
    );
  }

  return (
    <>
      <DataTable<PromptResponse> header={headers} data={tableData} isLoading={isLoading} />
      <Paginate
        isLoading={isLoading}
        itemCount={total}
        page={page - 1}
        pageSize={pageSize}
        numPages={totalPages}
        onPageClick={handlePageClick}
        onPageSizeChange={handlePageSizeChange}
        showPageSize
      />
    </>
  );
};

export default PromptsList;
